import { v4 as uuidv4 } from "uuid";
import { jsonrepair } from "jsonrepair";
import { iChatData } from "../types";
import { DATA_STREAMING_KEY } from "../constant/common";
import { recordCurrencyFromFlightSearch, recordCurrencyFromHotelSearch } from "./searchCurrency";
import { normalizeSupplyType, supplyTypeField } from "./booking/supplyType";

export const V2_RESPONSE_TYPE = {
  ai_assistant: "ai_assistant",
  flight: "flight",
  hotel: "hotel",
  activity: "activity",
} as const;

export type V2ResponseType = "ai_assistant" | "flight" | "hotel" | "activity" | "";

export interface V2BlockRenderResult {
  entryPatch: Partial<iChatData>;
  lastResponseText: string;
  renderPath: V2ResponseType;
  isRenderable: boolean;
}

function normalizeHotelResponse(parsed: any): any[] {
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed?.error === "string") {
    return [
      {
        hotels: [],
        error: parsed.error,
        errorStatus: parsed.errorStatus,
      },
    ];
  }
  if (Array.isArray(parsed?.data)) {
    return parsed.data.map((city: any) => ({
      ...city,
      uniqueId:
        city?.uniqueId ??
        city?.pagination?.uniqueId ??
        parsed.pagination?.uniqueId ??
        parsed.sessionId,
      pagination: city?.pagination ?? parsed.pagination,
      sessionId: city?.sessionId ?? parsed.sessionId,
      // Search-level DOMESTIC/INTERNATIONAL — stamped per block (each hotel block is its own
      // search) before the city|checkIn|checkOut dedupe, so a city keeps its own search's value.
      ...supplyTypeField(normalizeSupplyType(city?.supplyType) ?? parsed?.supplyType),
    }));
  }
  return [];
}

function normalizeActivityResponse(parsed: any): any[] {
  if (!parsed) return [];
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed?.error === "string") {
    return [
      {
        activities: [],
        error: parsed.error,
        errorStatus: parsed.errorStatus,
      },
    ];
  }
  const cities = parsed?.data?.cities || parsed?.cities;
  if (Array.isArray(cities)) return cities;
  return [];
}

export function extractAiAssistantTexts(blocks: any[]): string[] {
  if (!Array.isArray(blocks)) return [];
  return blocks
    .filter(
      (m: any) =>
        m?.type === "text" &&
        m?.source === "ai_assistant" &&
        typeof m?.text === "string" &&
        m.text.trim().length > 0,
    )
    .map((m: any) => m.text);
}

export function findRenderPayload(blocks: any[]): any {
  if (!Array.isArray(blocks)) return undefined;
  for (let i = blocks.length - 1; i >= 0; i--) {
    const m = blocks[i];
    if (m?.type === "text" && (m?.source === "hotel" || m?.source === "flight" || m?.source === "activity") && m?.text) return m;
  }
  return blocks[blocks.length - 1];
}

function tryParseJson(text: string | undefined, errorTag: string): any | null {
  if (typeof text !== "string" || text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`[v2BlockRenderer] ${errorTag} JSON parse failure`, e, text);
    return null;
  }
}

function normalizeFlightSearchRequest(input: any): {
  adults?: number;
  children?: number;
  infants?: number;
  tripType?: number;
  segments?: any[];
  cabin?: string;
  currency?: string;
} {
  if (!input || typeof input !== "object") return {};
  const flights = input.flights && typeof input.flights === "object" ? input.flights : undefined;

  const toCount = (x: any): number | undefined => {
    if (typeof x === "number" && Number.isFinite(x)) return x;
    if (typeof x === "string" && x.trim() !== "") {
      const n = Number(x);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  };

  const adultsFromLLM =
    toCount(input.number_of_adults ?? flights?.number_of_adults) ?? 0;
  const seniorsFromLLM =
    toCount(
      input.number_of_seniors ??
        flights?.number_of_seniors ??
        input.number_of_senior_citizens ??
        flights?.number_of_senior_citizens,
    ) ?? 0;
  const adults =
    adultsFromLLM + seniorsFromLLM > 0
      ? adultsFromLLM + seniorsFromLLM
      : toCount(input.adults ?? flights?.adults);
  const children = toCount(
    input.number_of_children ??
      flights?.number_of_children ??
      input.children ??
      flights?.children,
  );
  const infants = toCount(
    input.number_of_infants ??
      flights?.number_of_infants ??
      input.infants ??
      flights?.infants,
  );

  let tripType: number | undefined;
  const tripTypeStr = flights?.trip_type;
  if (typeof tripTypeStr === "string") {
    if (tripTypeStr === "oneway") tripType = 1;
    else if (tripTypeStr === "roundtrip") tripType = 2;
    else if (tripTypeStr === "multicity") tripType = 3;
  }
  if (tripType === undefined && typeof input.tripType === "number") {
    tripType = input.tripType;
  }

  const segments = Array.isArray(flights?.segments)
    ? flights.segments
    : Array.isArray(input.segments)
      ? input.segments
      : undefined;

  const cabin =
    typeof flights?.cabin_class === "string"
      ? flights.cabin_class
      : typeof input.cabin === "string"
        ? input.cabin
        : undefined;

  return {
    ...(adults !== undefined ? { adults } : {}),
    ...(children !== undefined ? { children } : {}),
    ...(infants !== undefined ? { infants } : {}),
    ...(tripType !== undefined ? { tripType } : {}),
    ...(segments !== undefined ? { segments } : {}),
    ...(cabin !== undefined ? { cabin } : {}),
    ...(typeof input.currency === "string" ? { currency: input.currency } : {}),
  };
}

function normalizeHotelSearchRequest(input: any): {
  noOfRooms?: number;
  adults?: number;
  children?: number;
  infants?: number;
  seniors?: number;
  childrenAges?: number[] | string;
  roomInfo?: any[];
  stays?: any[];
} {
  if (!input || typeof input !== "object") return {};
  const nested =
    (input.hotels && typeof input.hotels === "object" && input.hotels) ||
    (input.stays && typeof input.stays === "object" && input.stays) ||
    undefined;
  const src = nested || input;

  const toCount = (x: any): number | undefined => {
    if (typeof x === "number" && Number.isFinite(x)) return x;
    if (typeof x === "string" && x.trim() !== "") {
      const n = Number(x);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  };

  const directRoomInfo = Array.isArray(input.roomInfo)
    ? input.roomInfo
    : Array.isArray(src?.roomInfo)
      ? src.roomInfo
      : undefined;

  const noOfRooms = toCount(
    input.noOfRooms ??
      input.number_of_rooms ??
      input.rooms ??
      src?.noOfRooms ??
      src?.number_of_rooms ??
      src?.rooms,
  );
  const adults = toCount(
    input.number_of_adults ?? input.adults ?? src?.number_of_adults ?? src?.adults,
  );
  const children = toCount(
    input.number_of_children ?? input.children ?? src?.number_of_children ?? src?.children,
  );
  const infants = toCount(
    input.number_of_infants ?? input.infants ?? src?.number_of_infants ?? src?.infants,
  );
  const seniors = toCount(
    input.number_of_seniors ??
      input.number_of_senior_citizens ??
      src?.number_of_seniors ??
      src?.number_of_senior_citizens,
  );

  const rawAges =
    input.childrenAges ??
    input.children_ages ??
    input.child_ages ??
    src?.childrenAges ??
    src?.children_ages ??
    src?.child_ages;
  const childrenAges =
    Array.isArray(rawAges) || (typeof rawAges === "string" && rawAges.length > 0)
      ? rawAges
      : undefined;

  return {
    ...(directRoomInfo && directRoomInfo.length > 0 ? { roomInfo: directRoomInfo } : {}),
    ...(noOfRooms !== undefined ? { noOfRooms } : {}),
    ...(adults !== undefined ? { adults } : {}),
    ...(children !== undefined ? { children } : {}),
    ...(infants !== undefined ? { infants } : {}),
    ...(seniors !== undefined ? { seniors } : {}),
    ...(childrenAges !== undefined ? { childrenAges } : {}),
    ...(Array.isArray(src?.stays) && src.stays.length > 0 ? { stays: src.stays } : {}),
  };
}

export function extractAllToolResponses(
  blocks: any[],
  callSiteTag: string,
): { flightResponse?: any; hotelResponse?: any; activityResponse?: any; flightSearchRequest?: any; hotelSearchRequest?: any } {
  const result: { flightResponse?: any; hotelResponse?: any; activityResponse?: any; flightSearchRequest?: any; hotelSearchRequest?: any } = {};
  if (!Array.isArray(blocks)) return result;
  let foundFlight = false;
  let foundFlightReq = false;
  let foundHotelReq = false;
  let echoedHotelReq: ReturnType<typeof normalizeHotelSearchRequest> | undefined;
  const hotelCities: any[] = [];
  const activityCities: any[] = [];
  for (let i = blocks.length - 1; i >= 0; i--) {
    const m = blocks[i];
    if (
      !foundFlightReq &&
      m?.type === "tool_use" &&
      m?.name === "search_flights" &&
      m?.input &&
      typeof m.input === "object"
    ) {
      result.flightSearchRequest = normalizeFlightSearchRequest(m.input);
      foundFlightReq = true;
      const fsr = result.flightSearchRequest;
      if (
        fsr &&
        fsr.adults === undefined &&
        fsr.children === undefined &&
        fsr.infants === undefined
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[v2BlockRenderer] search_flights captured but no passenger counts resolved (${callSiteTag})`,
          {
            inputKeys: Object.keys(m.input),
            flightsKeys:
              m.input.flights && typeof m.input.flights === "object"
                ? Object.keys(m.input.flights)
                : null,
            rawInput: m.input,
          },
        );
      }
      continue;
    }
    if (
      !foundHotelReq &&
      m?.type === "tool_use" &&
      m?.name === "search_hotels" &&
      m?.input &&
      typeof m.input === "object"
    ) {
      result.hotelSearchRequest = normalizeHotelSearchRequest(m.input);
      foundHotelReq = true;
      const hsr = result.hotelSearchRequest;
      if (
        hsr &&
        hsr.noOfRooms === undefined &&
        hsr.adults === undefined &&
        !(Array.isArray(hsr.roomInfo) && hsr.roomInfo.length > 0)
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[v2BlockRenderer] search_hotels captured but no room/guest counts resolved (${callSiteTag})`,
          {
            inputKeys: Object.keys(m.input),
            hotelsKeys:
              m.input.hotels && typeof m.input.hotels === "object"
                ? Object.keys(m.input.hotels)
                : null,
            staysKeys:
              m.input.stays && typeof m.input.stays === "object"
                ? Object.keys(m.input.stays)
                : null,
            rawInput: m.input,
          },
        );
      }
      continue;
    }
    if (
      !foundHotelReq &&
      m?.type === "tool_use" &&
      m?.name === "search_hotels" &&
      m?.input &&
      typeof m.input === "object"
    ) {
      result.hotelSearchRequest = normalizeHotelSearchRequest(m.input);
      foundHotelReq = true;
      const hsr = result.hotelSearchRequest;
      if (
        hsr &&
        hsr.noOfRooms === undefined &&
        hsr.adults === undefined &&
        !(Array.isArray(hsr.roomInfo) && hsr.roomInfo.length > 0)
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[v2BlockRenderer] search_hotels captured but no room/guest counts resolved (${callSiteTag})`,
          {
            inputKeys: Object.keys(m.input),
            hotelsKeys:
              m.input.hotels && typeof m.input.hotels === "object"
                ? Object.keys(m.input.hotels)
                : null,
            staysKeys:
              m.input.stays && typeof m.input.stays === "object"
                ? Object.keys(m.input.stays)
                : null,
            rawInput: m.input,
          },
        );
      }
      continue;
    }
    if (m?.type !== "text" || typeof m?.text !== "string" || m.text.length === 0) continue;
    if (!foundFlight && m?.source === "flight") {
      result.flightResponse = tryParseJson(m.text, `[${callSiteTag}] flight`) ?? {};
      foundFlight = true;
    } else if (m?.source === "hotel") {
      const parsed = tryParseJson(m.text, `[${callSiteTag}] hotel`);
      const normalized = normalizeHotelResponse(parsed);
      if (normalized.length > 0) hotelCities.unshift(...normalized);
      if (!echoedHotelReq && parsed?.request && typeof parsed.request === "object") {
        const echoed = normalizeHotelSearchRequest(parsed.request);
        if (echoed.noOfRooms !== undefined || echoed.adults !== undefined || echoed.roomInfo?.length) {
          echoedHotelReq = echoed;
        }
      }
    } else if (m?.source === "activity") {
      const parsed = tryParseJson(m.text, `[${callSiteTag}] activity`);
      const normalized = normalizeActivityResponse(parsed);
      if (normalized.length > 0) activityCities.unshift(...normalized);
    }
  }
  const isBareErrorSentinel = (entry: any): boolean =>
    !!entry?.error && !entry?.city && (!Array.isArray(entry?.hotels) || entry.hotels.length === 0);
  const hasSuccessfulHotel = hotelCities.some((e: any) => !isBareErrorSentinel(e));
  const finalHotelCities = hasSuccessfulHotel
    ? hotelCities.filter((e: any) => !isBareErrorSentinel(e))
    : hotelCities;

  if (finalHotelCities.length > 0) {
    const byStay = new Map<string, any>();
    for (const c of finalHotelCities) {
      const key = `${c?.city ?? ""}|${c?.checkIn ?? ""}|${c?.checkOut ?? ""}`;
      byStay.set(key, c);
    }
    result.hotelResponse = Array.from(byStay.values());
  }
  if (activityCities.length > 0) result.activityResponse = activityCities;

  // chat-search v2 (LLM-function) sends no search_hotels tool_use block, but the
  // engine's own echo of the request rides inside the hotel result, so the
  // searched party still reaches the card header, detail panel and room pricing.
  if (!result.hotelSearchRequest && echoedHotelReq) result.hotelSearchRequest = echoedHotelReq;

  if (result.hotelResponse && !result.hotelSearchRequest) {
    // eslint-disable-next-line no-console
    console.warn(
      `[v2BlockRenderer] hotel result present but NO search_hotels tool_use block in stream (${callSiteTag}) — room count will fall back to bookingDetails ("1 room"). Backend may not be forwarding the tool_use block.`,
    );
  }

  if (result.hotelResponse) recordCurrencyFromHotelSearch(result.hotelResponse);
  if (result.flightResponse) recordCurrencyFromFlightSearch(result.flightResponse);
  return result;
}

interface RenderContext {
  traceId?: string;
  agentKey?: string;
  routerKey?: string;
  debugHotel?: boolean;
  callSiteTag?: string;
}

function safeParseEnvelopeJson(body: string): any | null {
  if (typeof body !== "string" || body.length === 0) return null;
  const start = body.indexOf("{");
  const end = body.lastIndexOf("}");
  const trimmed = start >= 0 && end > start ? body.slice(start, end + 1) : null;
  const attempts = [body];
  if (trimmed && trimmed !== body) attempts.push(trimmed);
  if (trimmed) {
    try {
      attempts.push(jsonrepair(trimmed));
    } catch {}
  }
  for (const attempt of attempts) {
    try {
      return JSON.parse(attempt);
    } catch {}
  }
  return null;
}

interface ParsedEnvelope {
  returnType: string;
  preText: string;
  postText: string;
  message: string;
  destinationList: any[];
}

const MAX_ENVELOPE_LEN = 100000;
const DESTINATION_PLACEHOLDER_IMAGE = "/destinationPlaceholder.png";

export function parseTaggedEnvelope(rawText: string): ParsedEnvelope | null {
  if (typeof rawText !== "string") return null;
  if (!/<return-type>|<destination-\d+>|<pretext>|<posttext>|<follow-?up>/i.test(rawText)) {
    return null;
  }
  const text = rawText.length > MAX_ENVELOPE_LEN ? rawText.slice(0, MAX_ENVELOPE_LEN) : rawText;

  const STATEMENT_KEY = DATA_STREAMING_KEY.STATEMENT.key;
  let returnType = "";
  let preText = "";
  let postText = "";
  let followUp = "";
  const destinationList: any[] = [];

  const collectRe = /<([a-z][a-z0-9_-]*)>([\s\S]*?)<\/\1>/gi;
  let match: RegExpExecArray | null;
  while ((match = collectRe.exec(text)) !== null) {
    const tag = match[1].toLowerCase();
    const obj = safeParseEnvelopeJson(match[2].trim());
    if (tag === DATA_STREAMING_KEY.EVENTS.RETURN_TYPE) {
      returnType = String(obj?.return_type ?? returnType);
    } else if (tag === DATA_STREAMING_KEY.INTENT.PRE_TEXT) {
      preText = String(obj?.[STATEMENT_KEY] ?? preText);
    } else if (tag === DATA_STREAMING_KEY.INTENT.POST_TEXT) {
      postText = String(obj?.[STATEMENT_KEY] ?? postText);
    } else if (tag === "follow-up" || tag === DATA_STREAMING_KEY.INTENT.FOLLOW_UP) {
      followUp = String(obj?.[STATEMENT_KEY] ?? followUp);
    } else if (/^destination-\d+$/.test(tag) && obj) {
      destinationList.push({
        ...obj,
        checkedFlag: false,
        checkFlag: false,
        id: uuidv4(),
        imageURL: DESTINATION_PLACEHOLDER_IMAGE,
      });
    } else if (obj && typeof obj?.[STATEMENT_KEY] === "string") {
      followUp = [followUp, obj[STATEMENT_KEY]].filter(Boolean).join("\n\n");
    }
  }

  const prose = text
    .replace(/<([a-z][a-z0-9_-]*)>[\s\S]*?<\/\1>/gi, "")
    .replace(/<[a-z][a-z0-9_-]*>[\s\S]*$/i, "")
    .replace(/<\/?[a-z][a-z0-9_-]*>/gi, "")
    .replace(/\{[^{}]*"intent"[^{}]*"(?:statement|return_type)"[^{}]*\}/gi, "")
    .replace(/\{[^{}]*"(?:statement|return_type)"[^{}]*"intent"[^{}]*\}/gi, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const message = [prose, followUp].filter(Boolean).join("\n\n").trim();
  return { returnType, preText, postText, message, destinationList };
}

function stripEnvelopeFromText(text: string): string {
  const parsed = parseTaggedEnvelope(text);
  if (!parsed) return text;
  return parsed.message || [parsed.preText, parsed.postText].filter(Boolean).join("\n\n");
}

const ENVELOPE_FALLBACK_MESSAGE =
  "Sorry, I couldn't display that response. Could you rephrase your request?";

export function renderV2BlocksToChatEntry(
  blocks: any[],
  ctx: RenderContext = {},
): V2BlockRenderResult {
  const { traceId, agentKey, routerKey, debugHotel = false, callSiteTag = "" } = ctx;
  const safeBlocks: any[] = Array.isArray(blocks) ? blocks : [];

  const lastResponseObj = findRenderPayload(safeBlocks);
  let responseType: V2ResponseType = (lastResponseObj?.source ?? "") as V2ResponseType;
  if (!lastResponseObj?.text) responseType = "";

  const allTools = extractAllToolResponses(safeBlocks, callSiteTag);

  const agentTrace = agentKey
    ? [{ key: agentKey } as { key: string; version?: string; subTools?: string[] }]
    : undefined;
  const routerTrace = routerKey ? { key: routerKey } : undefined;

  const baseTrace: Partial<iChatData> = {
    ...(agentTrace ? { agents: agentTrace } : {}),
    ...(routerTrace ? { router: routerTrace } : {}),
    ...(traceId ? { traceId } : {}),
  };

  switch (responseType) {
    case V2_RESPONSE_TYPE.ai_assistant: {
      const text = lastResponseObj?.text ?? "";
      const envelope = parseTaggedEnvelope(text);
      if (envelope) {
        const isDestinations =
          envelope.returnType === DATA_STREAMING_KEY.REUTRN_TYPE.DESTINATIONS &&
          envelope.destinationList.length > 0;
        const preText = envelope.preText || (isDestinations ? envelope.message : "");
        let message = isDestinations ? "" : envelope.message;
        if (!isDestinations && !message && !preText && !envelope.postText) {
          message = ENVELOPE_FALLBACK_MESSAGE;
        }
        return {
          entryPatch: {
            ...baseTrace,
            message,
            ...(preText ? { preText } : {}),
            ...(envelope.postText ? { postText: envelope.postText } : {}),
            responseType: envelope.returnType || "ai_assistant",
            isUserMessage: false,
            isLoading: false,
            destinationList: isDestinations ? envelope.destinationList : [],
            singleSelectedDestination: false,
          },
          lastResponseText: text,
          renderPath: "ai_assistant",
          isRenderable: true,
        };
      }
      return {
        entryPatch: {
          ...baseTrace,
          message: text,
          responseType: lastResponseObj?.source ? lastResponseObj.source : "ai_assistant",
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        lastResponseText: text,
        renderPath: "ai_assistant",
        isRenderable: true,
      };
    }
    case V2_RESPONSE_TYPE.flight: {
      const aiText = stripEnvelopeFromText(extractAiAssistantTexts(safeBlocks).join("\n\n"));
      return {
        entryPatch: {
          ...baseTrace,
          message: aiText,
          responseType: lastResponseObj?.source ? lastResponseObj.source : "ai_assistant",
          ...allTools,
          resultsGeneratedAt: Date.now(),
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        lastResponseText: lastResponseObj?.text ?? "",
        renderPath: "flight",
        isRenderable: true,
      };
    }
    case V2_RESPONSE_TYPE.hotel: {
      const aiText = stripEnvelopeFromText(extractAiAssistantTexts(safeBlocks).join("\n\n"));
      const hotelResponse = allTools.hotelResponse ?? [];

      if (debugHotel) {
        const firstCity = hotelResponse?.[0];
        const firstHotel = firstCity?.hotels?.[0];
        // eslint-disable-next-line no-console
        console.log(`[chat hotel response — raw text — ${callSiteTag}]`, lastResponseObj?.text);
        // eslint-disable-next-line no-console
        console.log(`[chat hotel response — parsed — ${callSiteTag}]`, hotelResponse);
        // eslint-disable-next-line no-console
        console.log(
          `[chat hotel response — top-level structure — ${callSiteTag}]`,
          {
            isArray: Array.isArray(hotelResponse),
            cityCount: Array.isArray(hotelResponse) ? hotelResponse.length : null,
            firstCityKeys: firstCity ? Object.keys(firstCity) : null,
            firstCityHotelCount: firstCity?.hotels?.length ?? null,
          },
        );
        // eslint-disable-next-line no-console
        console.log(
          `[chat hotel response — session ids — ${callSiteTag}]`,
          {
            cityLevelUniqueId: firstCity?.uniqueId ?? null,
            paginationUniqueId: firstCity?.pagination?.uniqueId ?? null,
            checkIn: firstCity?.checkIn,
            checkOut: firstCity?.checkOut,
            city: firstCity?.city,
          },
        );
        // eslint-disable-next-line no-console
        console.log(
          `[chat hotel response — pagination block — ${callSiteTag}]`,
          {
            atTopLevel: (hotelResponse as any)?.pagination ?? null,
            atDataWrapped: (hotelResponse as any)?.data?.pagination ?? null,
            atFirstCityLevel: firstCity?.pagination ?? null,
          },
        );
        // eslint-disable-next-line no-console
        console.log(
          `[chat hotel response — first hotel (top-level) — ${callSiteTag}]`,
          firstHotel
            ? {
                hotelId: firstHotel.hotelId,
                unicaId: firstHotel.unicaId,
                refundable: firstHotel.refundable,
                currency: firstHotel.currency,
                totalPrice: firstHotel.totalPrice,
                allKeys: Object.keys(firstHotel),
              }
            : null,
        );
        // eslint-disable-next-line no-console
        console.log(
          `[chat hotel response — first hotel.hotelDetails (nested) — ${callSiteTag}]`,
          firstHotel?.hotelDetails
            ? {
                name: firstHotel.hotelDetails.name,
                city: firstHotel.hotelDetails.city,
                address: firstHotel.hotelDetails.address,
                country: firstHotel.hotelDetails.country,
                stars: firstHotel.hotelDetails.stars,
                rating: firstHotel.hotelDetails.rating,
                reviewCount: firstHotel.hotelDetails.reviewCount,
                reviewHeading: firstHotel.hotelDetails.reviewHeading,
                hotelMainImage: firstHotel.hotelDetails.hotelMainImage,
                amenityCount: firstHotel.hotelDetails.hotelAmenities?.length ?? 0,
                imageCount: firstHotel.hotelDetails.hotelImages?.length ?? 0,
                allDetailsKeys: Object.keys(firstHotel.hotelDetails),
              }
            : null,
        );
      }

      return {
        entryPatch: {
          ...baseTrace,
          message: aiText,
          responseType: lastResponseObj?.source ? lastResponseObj.source : "ai_assistant",
          ...allTools,
          resultsGeneratedAt: Date.now(),
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        lastResponseText: lastResponseObj?.text ?? "",
        renderPath: "hotel",
        isRenderable: true,
      };
    }
    case V2_RESPONSE_TYPE.activity: {
      const aiText = stripEnvelopeFromText(extractAiAssistantTexts(safeBlocks).join("\n\n"));
      return {
        entryPatch: {
          ...baseTrace,
          message: aiText,
          responseType: lastResponseObj?.source ? lastResponseObj.source : "ai_assistant",
          ...allTools,
          resultsGeneratedAt: Date.now(),
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        lastResponseText: lastResponseObj?.text ?? "",
        renderPath: "activity",
        isRenderable: true,
      };
    }
    default:
      return {
        entryPatch: {
          ...baseTrace,
          responseType: "ai_assistant",
          isUserMessage: false,
          isLoading: false,
          destinationList: [],
          singleSelectedDestination: false,
        },
        lastResponseText: "",
        renderPath: "",
        isRenderable: false,
      };
  }
}
